const listeners = new Set();
const watched = new WeakSet();
let registration;
let available = false;
let reloadRequired = false;
let checking;
let lastCheck = 0;
let started = false;

export const isUpdateAvailable = () => available;
export function subscribeToUpdates(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce() {
  available = Boolean(reloadRequired || registration?.waiting);
  listeners.forEach((listener) => listener());
}

function bounded(promise, milliseconds = 15000) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("Actualizarea durează prea mult. Reîncearcă.")), milliseconds); }),
  ]).finally(() => clearTimeout(timer));
}

export function watchForUpdates(workerRegistration, config = {}) {
  registration = workerRegistration;
  if (watched.has(registration)) { announce(); return; }
  watched.add(registration);
  const watchInstalling = () => {
    const worker = registration.installing;
    if (!worker) return;
    const inspect = () => {
      if (worker.state !== "installed") return;
      announce();
      if (navigator.serviceWorker.controller) config.onUpdate?.(registration);
      else config.onSuccess?.(registration);
    };
    worker.addEventListener("statechange", inspect);
    inspect();
  };
  registration.addEventListener("updatefound", watchInstalling);
  watchInstalling();
  announce();
  if (!started) {
    started = true;
    let controller = navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      // Another tab can activate the worker. Offer a reload here, too, but
      // never discard this tab's unsaved form without an explicit action.
      if (controller) reloadRequired = true;
      controller = navigator.serviceWorker.controller;
      announce();
    });
    const check = () => {
      if (document.visibilityState !== "hidden") checkForUpdate(false).catch(() => {});
    };
    window.addEventListener("focus", check);
    window.addEventListener("online", () => checkForUpdate().catch(() => {}));
    document.addEventListener("visibilitychange", check);
    setInterval(check, 60000);
    check();
  }
}

export function checkForUpdate(force = true) {
  if (checking) return checking;
  if (navigator.onLine === false) return Promise.reject(new Error("Conectează-te la internet pentru a verifica actualizarea."));
  if (!force && Date.now() - lastCheck < 60000) return Promise.resolve(available);
  checking = (async () => {
    if (!registration && "serviceWorker" in navigator) {
      const found = await bounded(navigator.serviceWorker.getRegistration());
      if (found) watchForUpdates(found);
    }
    if (!registration) return false;
    lastCheck = Date.now();
    await bounded(registration.update());
    // update() can finish while the new worker is still installing. Its
    // statechange listener will announce availability when caching completes.
    announce();
    return available;
  })().finally(() => { checking = undefined; });
  return checking;
}

export async function applyUpdate(reload = () => window.location.reload()) {
  if (reloadRequired) { reload(); return; }
  const worker = registration?.waiting;
  if (!worker) throw new Error("Versiunea nouă nu este încă pregătită. Reîncearcă verificarea.");
  // Wait for the controller to change BEFORE reloading; reloading immediately
  // after SKIP_WAITING can load the old cached shell again.
  await new Promise((resolve, reject) => {
    const changed = () => { cleanup(); resolve(); };
    const timer = setTimeout(() => { cleanup(); reject(new Error("Actualizarea nu s-a activat. Reîncearcă.")); }, 20000);
    const cleanup = () => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", changed);
    };
    navigator.serviceWorker.addEventListener("controllerchange", changed);
    try { worker.postMessage({ type: "SKIP_WAITING" }); }
    catch (error) { cleanup(); reject(error); }
  });
  reload();
}
